import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { Button } from '../components/ui/button'
import ServiceCard from '../components/services/ServiceCard'
import LoadingAnimation from '../components/ui/LoadingAnimation'
import { api, endpoints } from '../lib/api'
import type { Service } from '../types'

interface ServicesPageState {
  view: 'main' | 'sub'
  selectedParentService: Service | null
}

const ServicesPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const isRTL = i18n.language === 'ar'
  
  // Initialize page state from location state (from homepage navigation) or default to main view
  const [pageState, setPageState] = useState<ServicesPageState>(() => {
    const locationState = location.state as any
    if (locationState?.view === 'sub' && locationState?.selectedParentService) {
      // Clear the navigation state to prevent issues on refresh
      window.history.replaceState({}, '')
      return {
        view: 'sub',
        selectedParentService: locationState.selectedParentService
      }
    }
    return {
      view: 'main',
      selectedParentService: null
    }
  })

  // Query for main services - only when in main view
  const { data: mainServicesResponse, isLoading: isLoadingMain } = useQuery({
    queryKey: ['main-services', i18n.language],
    queryFn: async () => {
      const response = await api.get(endpoints.mainServices)
      return response.data
    },
    enabled: pageState.view === 'main',
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1
  })

  // Query for sub-services - only when in sub view AND parent is selected
  const { data: subServicesResponse, isLoading: isLoadingSub } = useQuery({
    queryKey: ['sub-services', pageState.selectedParentService?.id, i18n.language],
    queryFn: async () => {
      if (!pageState.selectedParentService?.id) {
        throw new Error('No parent service selected')
      }
      const response = await api.get(
        endpoints.subServices(pageState.selectedParentService.id)
      )
      return response.data
    },
    enabled: pageState.view === 'sub' && 
             pageState.selectedParentService !== null && 
             !!pageState.selectedParentService?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1
  })

  // Only consider the appropriate loading state based on current view
  const isLoading = pageState.view === 'main' ? isLoadingMain : isLoadingSub
  
  // Transform API services to match our Service interface
  const transformApiService = (apiService: any): Service => ({
    id: apiService.id,
    parent_id: apiService.parent_id,
    name_en: apiService.name_en,
    name_ar: apiService.name_ar,
    title_en: apiService.name_en,
    title_ar: apiService.name_ar,
    description_en: apiService.description_en,
    description_ar: apiService.description_ar,
    localizedName: isRTL ? apiService.name_ar : apiService.name_en,
    localizedDescription: isRTL ? apiService.description_ar : apiService.description_en,
    category: apiService.category,
    icon: apiService.icon,
    image_url: apiService.image_url,
    tags: apiService.tags || [],
    is_active: true,
    display_order: apiService.display_order,
    has_children: apiService.has_children,
    children_count: apiService.children_count,
    children: apiService.children?.map(transformApiService),
    parent: apiService.parent,
    created_at: '',
    updated_at: ''
  })

  const currentServices: Service[] = (
    pageState.view === 'main' 
      ? mainServicesResponse?.data?.services || []
      : subServicesResponse?.data?.services || []
  ).map(transformApiService).filter((service: Service) => service.is_active)

  const handleServiceClick = (service: Service) => {
    if (pageState.view === 'main' && service.has_children) {
      // If it's a main service with children, show sub-services
      setPageState({
        view: 'sub',
        selectedParentService: service
      })
    } else {
      // If it's a service without children or a sub-service, navigate to details
      navigate(`/services/${service.id}`)
    }
  }

  const handleBackToMain = () => {
    setPageState({
      view: 'main',
      selectedParentService: null
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <LoadingAnimation size="lg" />
            <p className="text-muted-foreground mt-4">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen py-16 px-4 bg-gradient-to-b from-broxel-green/15 via-transparent to-broxel-blue/20">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          {pageState.view === 'sub' && pageState.selectedParentService && (
            <div className="mb-6">
              <Button
                variant="ghost"
                onClick={handleBackToMain}
                className="mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.back')}
              </Button>
              <div className="flex items-center justify-center text-sm text-muted-foreground mb-2">
                <span>{t('services.title')}</span>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span>
                  <span className="inline-flex items-center gap-1" dir="ltr">
                    <img src="/images/branding/b-logo.webp" alt="B" className="h-4 w-4 inline-block" />
                    <span>{(pageState.selectedParentService.localizedName || (isRTL ? pageState.selectedParentService.title_ar : pageState.selectedParentService.title_en)).split(' ')[0]}</span>
                  </span>
                  {(pageState.selectedParentService.localizedName || (isRTL ? pageState.selectedParentService.title_ar : pageState.selectedParentService.title_en)).split(' ').slice(1).join(' ') && (
                    <span className="ml-1">{(pageState.selectedParentService.localizedName || (isRTL ? pageState.selectedParentService.title_ar : pageState.selectedParentService.title_en)).split(' ').slice(1).join(' ')}</span>
                  )}
                </span>
              </div>
            </div>
          )}
          
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            {pageState.view === 'main' 
              ? t('services.title')
              : pageState.selectedParentService?.localizedName || (isRTL ? pageState.selectedParentService?.title_ar : pageState.selectedParentService?.title_en)
            }
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {pageState.view === 'main'
              ? t('services.subtitle')
              : pageState.selectedParentService?.localizedDescription || (isRTL ? pageState.selectedParentService?.description_ar : pageState.selectedParentService?.description_en)
            }
          </p>
        </div>

        {/* Services Grid */}
        {currentServices.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-muted-foreground">
              <p className="text-lg">{t('services.noResults.title')}</p>
              <p className="text-sm">{t('services.noResults.description')}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentServices.map((service: Service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onServiceClick={handleServiceClick}
                showChildren={pageState.view === 'main'}
              />
            ))}
          </div>
        )}
        
        {/* Call to Action */}
        <div className="mt-16 text-center bg-primary/5 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            {t('services.cta.title')}
          </h2>
          <p className="text-muted-foreground mb-6">
            {t('services.cta.description')}
          </p>
          <Button onClick={() => navigate('/contact')} size="lg">
            {t('services.cta.button')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ServicesPage